import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import defaultShareroom from "@assets/images/default_shareroom.png";
import { Path } from "@core/routing/path";
import { Group, GroupMemberRole } from "@domain/group/group";
import { ImageVersions } from "@domain/media/dtos";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { Clamp } from "@ui/components/containers/clamp";
import { Primary } from "@ui/components/containers/primary";
import { row, Stack, stack } from "@ui/components/layout";
import { MembersButton } from "@ui/components/profile/community/membersButton";
import { AccessModeIndicator } from "./accessModeIndicator";

interface ShareroomThumbnailUtraSmall extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
}

export const ShareroomThumbnailUtraSmall: React.FC<ShareroomThumbnailUtraSmall> = ({ shareroom, ...htmlProps }) => {
	const { banner, userRole, administrators: teachersList } = shareroom;
	const isOpen = shareroom.visibility === "PUBLIC";
	return (
		<Container {...htmlProps}>
			<Link to={Path.Shareroom(shareroom)}>
				<StyledBackgroundImage
					image={banner}
					defaultUrl={!banner ? defaultShareroom : undefined}
					size={ImageVersions.MEDIUM}
				/>
				<InfosWrapper>
					<Stack gap={2}>
						<StyledPrimaryBold size={2}>{shareroom.name}</StyledPrimaryBold>
						<Info role={userRole}>{I18n.t(`group.${shareroom.type}.indicative_role.${shareroom.userRole}`)}</Info>
					</Stack>
				</InfosWrapper>
			</Link>
			<StyledAccessModeIndicator closed={!isOpen} />
			<StyledMemberButton sample={teachersList} count={4} spacing={-13} imageSize="default" />
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	width: 100%;
	overflow: hidden;
	> a {
		width: 100%;
		height: 100%;
		${row(0, "flex-start", "stretch")};
		> * {
			flex-grow: 0;
			flex-shrink: 0;
		}
		gap: 10px;
	}
`;

const InfosWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	width: 60%;
	${stack(10)};
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	width: 80px;
	height: 80px;
	> :first-child {
		border-radius: 6px;
	}
`;

const StyledPrimaryBold = styled(Clamp)`
	${({ theme }) => theme.textStyles.primaryBold};
	font-size: 0.625rem;
`;

const Info = styled(Primary)<{ role?: GroupMemberRole }>`
	background: ${({ theme, role }) => (role === "MEMBER" ? theme.colors.thirded : theme.colors.secondary)};
	color: #fff;
	font-size: 0.5rem;
	padding: 3px;
	border-radius: 5px;
`;

const StyledAccessModeIndicator = styled(AccessModeIndicator)`
	position: absolute;
	top: 0;
	right: 0px;
	z-index: 1;
`;

const StyledMemberButton = styled(MembersButton)`
	position: absolute;
	bottom: 0;
	left: 88px;
`;
